import { useState, useRef, useEffect } from 'react';
import * as S from './styled';
//Components 
import CancelButton from '../../components/Button/CancelButton';
import ActionButtonLoad from '../../components/Button/ActionButtonLoad';
//Icons
import { Minus, X, Circle } from '@phosphor-icons/react';

interface ILayoutModal {
    title: string;
    subTitle: string;
    children: React.ReactNode;
    setModalClose: () => void;
    submiteModal: any;
    statusLoad: boolean;
    nameSubmit?: string;
    nameCancel?: string;
    initialPosition?: { x: number, y: number };

    nameIconMinimize?: any;
    setMaximizeModal?: () => void;
}

export default function LayoutModal(
    {
        title,
        subTitle,
        children,
        setModalClose,
        submiteModal,
        nameSubmit = 'Salvar',
        nameCancel = 'Cancelar',
        statusLoad,
        initialPosition,

        nameIconMinimize,
        setMaximizeModal
    }: ILayoutModal) {

    // 1. Crie um estado para controlar se o modal foi movido pelo usuário
    const [hasBeenMoved, setHasBeenMoved] = useState(false);

    // Referência para o elemento do modal
    const modalRef = useRef<HTMLDivElement>(null);

    // Estado para controlar a posição do modal
    const [position, setPosition] = useState(() => {
        // Posição inicial centrada por padrão
        if (initialPosition) return initialPosition;
        return { x: 0, y: 0 };
    });

    // Estado para controlar se o modal está sendo arrastado
    const [isDragging, setIsDragging] = useState(false);

    // Estados para armazenar a posição inicial do mouse ao começar a arrastar
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Estados para armazenar a ação de minimizar modal
    const [isMinimized, setIsMinimized] = useState(false);

    // Função para iniciar o arrasto do modal
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof HTMLElement) {
            // Só permite arrastar se o clique for no cabeçalho
            // e não em botões ou outros elementos interativos
            if (e.target.closest('.modal-header') &&
                !e.target.closest('.close-button')) {
                setIsDragging(true);

                // Calcula o deslocamento do mouse em relação ao modal
                const rect = modalRef.current?.getBoundingClientRect();
                if (rect) {
                    setDragOffset({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top
                    });
                }
            }
        }
    };

    // Função para arrastar o modal
    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && modalRef.current) {
            // Calcula a nova posição baseada na posição do mouse
            // menos o deslocamento inicial
            const rect = modalRef.current.getBoundingClientRect();
            const containerRect = modalRef.current.parentElement?.getBoundingClientRect();

            if (containerRect) {
                // Calcula as novas coordenadas
                let newX = e.clientX - dragOffset.x;
                let newY = e.clientY - dragOffset.y;

                // Limita a posição para não sair da tela
                const maxX = containerRect.width - rect.width;
                const maxY = containerRect.height - rect.height;

                newX = Math.max(0, Math.min(newX, maxX));
                newY = Math.max(0, Math.min(newY, maxY));

                setPosition({ x: newX, y: newY });
            }
        }
    };

    // 2. Modifique a função handleMouseUp para marcar que o modal foi movido
    const handleMouseUp = () => {
        if (isDragging) {
            setHasBeenMoved(true);
        }
        setIsDragging(false);
    };

    // Adiciona os event listeners quando o componente é montado
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        // Limpa os event listeners quando o componente é desmontado
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // 3. Modifique o useEffect de centralização para considerar se o modal já foi movido
    // Centraliza o modal quando é aberto inicialmente ou quando há mudanças de conteúdo
    useEffect(() => {
        if (modalRef.current && !hasBeenMoved) {
            const modalElement = modalRef.current;
            const containerElement = modalElement.parentElement;

            if (containerElement) {
                const containerRect = containerElement.getBoundingClientRect();
                const modalRect = modalElement.getBoundingClientRect();

                const centerX = (containerRect.width - modalRect.width) / 2;
                const centerY = (containerRect.height - modalRect.height) / 2;

                setPosition({ x: centerX, y: centerY });
            }
        }
    }, [hasBeenMoved, children]);

    const minimizeModal = () => {
        setIsMinimized(true);
    };

    const maximizeModal = () => {
        setIsMinimized(false);
        // Chama a função recebida como prop do componente pai
        setMaximizeModal && setMaximizeModal();
    };

    return (
        <>
            {
                isMinimized ? (
                    <S.ContentMinimize onClick={maximizeModal}>
                        <Circle size={14} weight="fill" color='#24BA5B' />
                        {nameIconMinimize}
                    </S.ContentMinimize>
                ) :
                    <S.Container>
                        <S.Content
                            isStatic={isDragging}
                            ref={modalRef}
                            style={{
                                left: `${position.x}px`,
                                top: `${position.y}px`,
                                cursor: isDragging ? 'grabbing' : 'default'
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            <S.Header className="modal-header">
                                <S.infor>
                                    <S.Title>
                                        {title}
                                    </S.Title>
                                    <S.SubTitle>
                                        {subTitle}
                                    </S.SubTitle>
                                </S.infor>
                                <S.ContentButton className="close-button">
                                    <Minus size={24} onClick={minimizeModal} />
                                    <X size={24} onClick={setModalClose} />
                                </S.ContentButton>
                            </S.Header>

                            {children}
                            <S.Footer>
                                <div>
                                    <CancelButton name={nameCancel} onClick={setModalClose} />
                                    <ActionButtonLoad name={nameSubmit} onClick={submiteModal} status={statusLoad} />
                                </div>
                            </S.Footer>

                        </S.Content>
                    </S.Container>
            }

        </>

    );
}